import React, { useEffect, useState } from "react";
import { ChevronDown, RotateCcw } from "lucide-react";

/* Per-file requirements extraction progress.
 *
 * Draws the `extraction` object the requirements/alignment endpoint puts on
 * every document (rfp/services/insights_dispatch.extraction_progress). The steps
 * are the pipeline's own, published as it reaches them, and a large file moves
 * once per finished part — nothing here runs on a timer, so a bar that has moved
 * has genuinely moved.
 *
 * A file whose run stopped short offers Resume (its finished steps or parts are
 * kept, so it continues from there) or Retry (nothing finished worth keeping). */

const STEP_LABEL = {
  reading: "Reading the document",
  extracting: "Pulling out requirements",
  checking: "Checking for anything missed",
  tidying: "Removing duplicates",
};

const TONE = {
  active: { bar: "bg-[linear-gradient(90deg,#8B5CF6,#6366F1)]", text: "text-[#7C3AED]" },
  done: { bar: "bg-[#2DAD4E]", text: "text-[#1F7D38]" },
  warn: { bar: "bg-[#F59E0B]", text: "text-[#B45309]" },
  error: { bar: "bg-[#DC2626]", text: "text-[#B91C1C]" },
  muted: { bar: "bg-[#D4D4D8]", text: "text-[#71717A]" },
};

const countLabel = (n) => (n === 0 ? "No requirements found" : `${n} requirement${n === 1 ? "" : "s"}`);

// "while checking for anything missed", "at part 6 of 8"
const whereItStopped = (x) => {
  if (x.passes_total && x.failed_step === "extracting") {
    return `at part ${Math.min((x.passes_done || 0) + 1, x.passes_total)} of ${x.passes_total}`;
  }
  const step = STEP_LABEL[x.failed_step];
  return step ? `while ${step.charAt(0).toLowerCase()}${step.slice(1)}` : "";
};

const describe = (x) => {
  switch (x.state) {
    case "queued":
      return { label: "Queued", tone: "muted", width: x.progress };
    case "running": {
      if (!x.phase) {
        // A large file is handed to its own one-at-a-time worker and can wait
        // for it; the server sends its part count while it does.
        const label = x.passes_total ? "Waiting for the large-file worker" : "Starting";
        return { label, tone: "active", width: x.progress };
      }
      let label = STEP_LABEL[x.phase] || "Working";
      if (x.phase === "extracting" && x.passes_total) {
        label += ` · part ${Math.min(x.passes_done + 1, x.passes_total)} of ${x.passes_total}`;
      }
      return { label, tone: "active", width: x.progress };
    }
    case "done":
      return { label: countLabel(x.requirements), tone: "done", width: 100 };
    case "partial": {
      const gap =
        x.reason === "parts_failed" && x.failed_passes?.length
          ? `${x.failed_passes.length} of ${x.passes_total} parts failed`
          : "partly extracted";
      return { label: `${countLabel(x.requirements)} · ${gap}`, tone: "warn", width: 100 };
    }
    case "failed": {
      const what =
        x.reason === "interrupted" ? "Stopped" : x.reason === "timed_out" ? "Timed out" : "Failed";
      const where = whereItStopped(x);
      return { label: where ? `${what} ${where}` : what, tone: "error", width: 100 };
    }
    case "ingesting":
      return { label: "Still processing the file", tone: "muted", width: 0 };
    case "skipped":
      return { label: "Questionnaire, skipped", tone: "muted", width: 0 };
    default:
      return { label: "Not extracted", tone: "muted", width: 0 };
  }
};

const IN_FLIGHT = ["queued", "running"];
const NEEDS_ACTION = ["failed", "partial"];
// What is still moving or needs a decision first; finished files last.
const ORDER = ["running", "queued", "failed", "partial", "idle", "ingesting", "done", "skipped"];

/* Documents that carry progress (a backend without it sends none, and then
 * nothing is drawn rather than a list of empty bars). */
export const progressDocs = (documents) =>
  (documents || [])
    .filter((d) => d && d.extraction)
    .sort((a, b) => ORDER.indexOf(a.extraction.state) - ORDER.indexOf(b.extraction.state));

export const summarize = (documents) => {
  const docs = progressDocs(documents).filter((d) => d.extraction.state !== "skipped");
  const settled = (d) => ["done", "partial"].includes(d.extraction.state);
  return {
    total: docs.length,
    done: docs.filter(settled).length,
    inFlight: docs.filter((d) => IN_FLIGHT.includes(d.extraction.state)).length,
    needsAction: docs.filter((d) => NEEDS_ACTION.includes(d.extraction.state)),
    // A failed file adds nothing, so the overall bar stops short of full until
    // it is resumed — it would be a lie to fill it.
    overall: docs.length
      ? Math.round(
          docs.reduce((s, d) => s + (settled(d) ? 100 : d.extraction.state === "failed" ? 0 : d.extraction.progress || 0), 0) /
            docs.length,
        )
      : 0,
  };
};

/* Keys clicked a moment ago, until the server shows them queued again — so a
 * second click can't fire before the next poll lands. */
const usePendingKeys = (documents) => {
  const [pending, setPending] = useState(() => new Set());
  useEffect(() => {
    if (pending.size === 0) return;
    const started = new Set(
      progressDocs(documents)
        .filter((d) => !NEEDS_ACTION.includes(d.extraction.state) && d.extraction.state !== "idle")
        .map((d) => d.doc_key),
    );
    const still = [...pending].filter((k) => !started.has(k));
    if (still.length !== pending.size) setPending(new Set(still));
  }, [documents, pending]);
  // Never stuck disabled: if the request failed, the button comes back.
  useEffect(() => {
    if (pending.size === 0) return;
    const t = setTimeout(() => setPending(new Set()), 15000);
    return () => clearTimeout(t);
  }, [pending]);
  const add = (keys) => setPending((p) => new Set([...p, ...keys]));
  return [pending, add];
};

const ActionButton = ({ children, onClick, disabled, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className="inline-flex flex-shrink-0 items-center gap-1 rounded border border-[#DEDEDE] bg-white px-2 py-0.5 text-[12px] font-medium text-[#A04A97] transition-colors hover:bg-[#FBF0F9] disabled:cursor-not-allowed disabled:opacity-50"
  >
    <RotateCcw size={12} />
    {children}
  </button>
);

const FileRow = ({ doc, onResume, pending }) => {
  const x = doc.extraction;
  const { label, tone, width } = describe(x);
  const t = TONE[tone];
  const active = tone === "active" || x.state === "queued";
  const canAct = onResume && (NEEDS_ACTION.includes(x.state) || x.state === "idle");
  const action = x.state === "idle" ? "Extract" : x.resumable ? "Resume" : "Retry";
  const hint =
    x.state === "idle"
      ? "Extract this file's requirements"
      : x.resumable
        ? "Continue from where it stopped — finished steps are kept"
        : "Run this file again";
  return (
    <li className="py-2">
      <div className="flex items-center justify-between gap-3 text-[13px]">
        <span className="min-w-0 truncate text-[#3F3F46]" title={doc.doc_name}>
          {doc.doc_name}
        </span>
        <span className="flex flex-shrink-0 items-center gap-2">
          <span className={t.text}>{label}</span>
          {canAct && (
            <ActionButton onClick={() => onResume([doc.doc_key])} disabled={pending} title={hint}>
              {pending ? "Starting" : action}
            </ActionButton>
          )}
        </span>
      </div>
      <div
        className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-[#F1EEF4]"
        role="progressbar"
        aria-label={`${doc.doc_name}: ${label}`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={width}
      >
        <div
          className={`h-full rounded-full transition-[width] duration-700 ease-out ${t.bar} ${active ? "animate-pulse" : ""}`}
          style={{ width: `${Math.max(width, active ? 3 : 0)}%` }}
        />
      </div>
    </li>
  );
};

/* The list on its own: used inside the processing card and the Run Analysis card. */
export const ExtractionProgressList = ({ documents, onResume, className = "" }) => {
  const [pending, addPending] = usePendingKeys(documents);
  const docs = progressDocs(documents);
  if (docs.length === 0) return null;
  const resume = onResume
    ? (keys) => {
        addPending(keys);
        onResume(keys);
      }
    : null;
  return (
    <ul className={`divide-y divide-[#F1EEF4] ${className}`}>
      {docs.map((d) => (
        <FileRow key={d.doc_key || d.doc_id} doc={d} onResume={resume} pending={pending.has(d.doc_key)} />
      ))}
    </ul>
  );
};

/* Collapsible panel above the requirements table. Requirements from files that
 * are done show in the table straight away; this keeps the rest in view —
 * still extracting, or stopped short and waiting for a Resume. */
export const ExtractionProgressPanel = ({ documents, running, onResume }) => {
  const [open, setOpen] = useState(true);
  const [pending, addPending] = usePendingKeys(documents);
  const { total, done, inFlight, needsAction, overall } = summarize(documents);
  if (total === 0 || (!running && inFlight === 0 && needsAction.length === 0)) return null;

  const stopped = needsAction.length;
  let headline;
  if (inFlight > 0) {
    headline = `Extracting requirements · ${done} of ${total} files done`;
    if (stopped) headline += ` · ${stopped} stopped`;
  } else if (stopped) {
    headline = `${stopped} file${stopped === 1 ? "" : "s"} stopped short. Requirements from the rest are below.`;
  } else {
    headline = "All files read · scoring alignment";
  }

  const resume = onResume
    ? (keys) => {
        addPending(keys);
        onResume(keys);
      }
    : null;
  const stoppedKeys = needsAction.map((d) => d.doc_key).filter((k) => !pending.has(k));

  return (
    <div className="mb-3 flex-shrink-0 rounded-lg border border-[#E9D5FF] bg-white">
      <div className="flex items-center gap-3 px-4 py-2.5">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          aria-expanded={open}
        >
          {(running || inFlight > 0) && (
            <span className="h-3.5 w-3.5 flex-shrink-0 rounded-full border-2 border-[#E9D5FF] border-t-[#8B5CF6] animate-spin" />
          )}
          <span className="truncate text-[13px] font-medium text-[#3F3F46]">{headline}</span>
          <ChevronDown
            size={16}
            className={`ml-auto flex-shrink-0 text-[#71717A] transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
        {resume && stoppedKeys.length > 1 && (
          <ActionButton onClick={() => resume(stoppedKeys)} title="Resume every file that stopped short">
            Resume all ({stoppedKeys.length})
          </ActionButton>
        )}
      </div>
      <div className="px-4 pb-2">
        <div
          className="h-1 w-full overflow-hidden rounded-full bg-[#F1EEF4]"
          role="progressbar"
          aria-label="All files"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={overall}
        >
          <div
            className="h-full rounded-full bg-[linear-gradient(90deg,#8B5CF6,#6366F1)] transition-[width] duration-700 ease-out"
            style={{ width: `${overall}%` }}
          />
        </div>
      </div>
      {open && (
        <ul className="max-h-[220px] overflow-y-auto border-t border-[#F1EEF4] px-4 custom-scrollbar divide-y divide-[#F1EEF4]">
          {progressDocs(documents).map((d) => (
            <FileRow key={d.doc_key || d.doc_id} doc={d} onResume={resume} pending={pending.has(d.doc_key)} />
          ))}
        </ul>
      )}
    </div>
  );
};
